import React, { useEffect, useState } from 'react';
import { Btn, DataTable, Loading, Msg, Select, TextInput, fmtDate, useAdminGet } from '../../kit';
import { AdminLink } from '../../adminNav';
import { Badge } from '../emailKit';
import Timeline from '../people/Timeline';
import type { TimelineTarget } from '../people/shared';
import { CtaCell, EngTags, ModePill, SECTION, SOURCES, SOURCE_HINT, SOURCE_WORD, arr, num, str } from './shared';

/**
 * Individual sends (email/stats_sends): every e-mail sent to one person, from
 * every ledger — campaigns, MEMAC, the automatic mails, researcher outreach,
 * and mail known only from SendGrid's events — newest first, server-paged.
 * A row opens that person's timeline (the People drawer) or its campaign.
 * Automated engagement is tagged on the row — "Instant read (auto)", "Apple
 * MPP", "Scanner open (auto)", "Bot click" — with the reason on hover (user
 * agent, network, seconds after delivery); opened / clicked are people's
 * unless the page's "Include automated" is on.
 */

// What stats_sends' status filter takes (admin_email_stats.STATUS_FILTERS):
// a row status, or how far the mail got.
const STATUSES = ['', 'sent', 'delivered', 'opened', 'clicked', 'not_opened', 'auto_opened', 'bot_clicked',
  'bounced', 'failed', 'queued', 'skipped'];
const STATUS_LABELS: Record<string, string> = {
  '': 'Any outcome', sent: 'Sent', delivered: 'Delivered', opened: 'Opened', clicked: 'Clicked',
  not_opened: 'Delivered, not opened (HTML)', auto_opened: 'Opened only by machines (instant / MPP / scanner)',
  bot_clicked: 'Clicked only by bots', bounced: 'Bounced', failed: 'Not sent (failed)',
  queued: 'Still queued', skipped: 'Skipped',
};
const SOURCE_LABELS: Record<string, string> = { '': 'Every source', ...SOURCE_WORD };
const PER_PAGE = ['50', '100', '250', '500'];

const flag = (on: any, label: string, tone: 'blue' | 'green' | 'red' | 'amber') =>
  (on ? <Badge tone={tone}>{label}</Badge> : null);

const SendsTable: React.FC<{
  filters: Record<string, string>; tplName: (k: string) => string; stamp: number;
  /** A status the page asked for (a KPI click), applied on arrival. */
  preset?: { status?: string; q?: string; nonce: number } | null;
}> = ({ filters, tplName, stamp, preset }) => {
  // Seeded from the preset, so arriving from a KPI fetches once, not twice.
  const [q, setQ] = useState(preset?.q || '');
  const [qLive, setQLive] = useState((preset?.q || '').trim());
  const [status, setStatus] = useState(preset?.status || '');
  const [source, setSource] = useState('');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [timeline, setTimeline] = useState<TimelineTarget | null>(null);

  useEffect(() => {
    if (!preset) return;
    if (preset.status !== undefined) setStatus(preset.status);
    if (preset.q !== undefined) { setQ(preset.q); setQLive(preset.q); }
  }, [preset?.nonce]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => setQLive(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);
  const fKey = JSON.stringify(filters);
  useEffect(() => { setPage(0); }, [fKey, qLive, status, source, perPage]);

  // The server pages from 1; this table counts from 0 like the others.
  const params: Record<string, string | number> = { ...filters, page: page + 1, per_page: perPage };
  if (qLive) params.q = qLive;
  if (status) params.status = status;
  if (source) params.source = source;
  const res = useAdminGet(SECTION, 'stats_sends', params, [fKey, qLive, status, source, page, perPage, stamp]);
  const d: any = res.data;
  const raw: any[] = Array.isArray(d) ? d : arr(d?.rows ?? d?.sends ?? d?.items);
  const total = num(d?.total ?? raw.length);
  const per = Number(perPage);
  const pages = Math.max(1, num(d?.pages) || Math.ceil(total / per) || 1);
  const bySource: Record<string, number> = (d?.counts?.by_source && typeof d.counts.by_source === 'object')
    ? d.counts.by_source : {};

  // The row carries the person's name and the template's; the server spells
  // the template one template_name, and `name` is the person's when both exist.
  const rows = raw.map(r => ({
    ...r,
    at: r.at ?? r.sent_at ?? r.created_at,
    person: str(r.person_name ?? r.user_name ?? (r.template_name !== undefined ? r.name : '')),
    email_name: str(r.template_name) || tplName(str(r.template_key)) || str(r.subject),
    outcome: '',
  }));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <TextInput value={q} onChange={setQ} placeholder="Search an address, a name or a subject…" wide />
        <Select value={status} onChange={setStatus} options={STATUSES} labels={STATUS_LABELS} />
        <Select value={source} onChange={setSource} options={['', ...SOURCES]} labels={SOURCE_LABELS} />
        <Btn small onClick={res.reload}>Refresh</Btn>
      </div>
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {Object.keys(bySource).length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap text-xs text-grayscale-1100">
          <span>By source:</span>
          {Object.entries(bySource).sort((a, b) => num(b[1]) - num(a[1])).map(([k, n]) => (
            <button key={k} type="button" onClick={() => setSource(s => (s === k ? '' : k))}
              title={`${SOURCE_HINT[k] || k} — click to show only these`}
              className={`rounded-full border px-2 py-0.5 ${source === k ? 'border-primary-500 bg-primary-100 text-grayscale-1300'
                : 'border-grayscale-400 hover:bg-grayscale-200'}`}>
              {SOURCE_WORD[k] || k} <b>{num(n).toLocaleString()}</b>
            </button>
          ))}
        </div>
      )}
      {res.loading && !res.data ? <Loading /> : (
        <>
          <DataTable rows={rows} tableId="email_dash_sends" maxHeight="40rem"
            columns={['at', 'email', 'person', 'email_name', 'mode', 'source', 'outcome']}
            optionalColumns={['subject', 'primary_cta', 'cta_clicked', 'template_key', 'send_id', 'trigger_id',
              'category', 'user_id', 'status', 'error']}
            labels={{ at: 'Sent', email: 'To', person: 'Name', email_name: 'E-mail', outcome: 'What happened',
              template_key: 'Template key', send_id: 'Campaign', trigger_id: 'MEMAC trigger',
              primary_cta: 'Primary CTA', cta_clicked: 'Clicked the CTA' }}
            unsortable={['outcome']}
            render={{
              at: r => <span title={str(r.at)}>{fmtDate(r.at)}</span>,
              email: r => (
                <button type="button" title="Open this person's timeline"
                  onClick={() => setTimeline(r.user_id ? { userId: str(r.user_id), email: str(r.email) || undefined }
                    : { email: str(r.email).toLowerCase() })}
                  className="text-left hover:underline underline-offset-2">{r.email || '—'}</button>
              ),
              email_name: r => <span title={str(r.template_key) || undefined}>{r.email_name || '—'}</span>,
              mode: r => <ModePill mode={r.mode} />,
              source: r => (r.source
                ? <span title={SOURCE_HINT[str(r.source)]}><Badge tone={r.source === 'event' ? 'amber' : 'grey'}>
                    {SOURCE_WORD[str(r.source)] || str(r.source)}</Badge></span>
                : <span className="text-grayscale-1100">—</span>),
              subject: r => <span className="text-xs text-grayscale-1200" title={str(r.subject)}>{r.subject || '—'}</span>,
              primary_cta: r => <CtaCell row={r} />,
              cta_clicked: r => (r.cta_clicked === null || r.cta_clicked === undefined
                ? <span className="text-grayscale-1100">{r.primary_cta ? 'n/a' : '—'}</span>
                : r.cta_clicked ? <Badge tone="green">yes</Badge> : <span className="text-grayscale-1100">no</span>),
              outcome: r => {
                const failed = /fail|error|not_sent|skipped/i.test(str(r.status));
                return (
                  <span className="inline-flex gap-1 flex-wrap">
                    {failed && <span title={str(r.error) || undefined}><Badge tone="red">{r.status === 'skipped' ? 'skipped' : 'not sent'}</Badge></span>}
                    {r.status === 'queued' && <Badge tone="amber">queued</Badge>}
                    {flag(r.delivered, 'delivered', 'blue')}
                    {flag(r.opened, 'opened', 'green')}
                    {flag(r.clicked, 'clicked', 'green')}
                    {flag(r.bounced, 'bounced', 'red')}
                    {flag(r.unsubscribed, 'unsubscribed', 'amber')}
                    <EngTags row={r} />
                    {!failed && r.status !== 'queued' && !r.delivered && !r.opened && !r.clicked && !r.bounced
                      && <Badge tone="grey">no events yet</Badge>}
                  </span>
                );
              },
              send_id: r => (r.send_id && r.send_id !== 'auto'
                ? <AdminLink to={{ group: 'email', tab: 'campaigns', campaign: str(r.send_id) }}>{str(r.send_id).slice(0, 10)}</AdminLink>
                : <span className="text-grayscale-1100">—</span>),
            }}
            actions={r => (
              <Btn small title="Everything we sent this person, and what they did"
                onClick={() => setTimeline(r.user_id ? { userId: str(r.user_id), email: str(r.email) || undefined }
                  : { email: str(r.email).toLowerCase() })}>Timeline</Btn>
            )}
            empty={qLive || status || source ? 'No send matches these filters.' : 'Nothing was sent in this period.'} />
          <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-grayscale-1100">
            <span>
              Page {page + 1} of {pages} · showing {rows.length ? page * per + 1 : 0}–{page * per + rows.length} of{' '}
              {total.toLocaleString()} send{total === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={perPage} onChange={setPerPage} options={PER_PAGE}
                labels={{ '50': '50 / page', '100': '100 / page', '250': '250 / page' }} />
              <Btn small onClick={() => setPage(0)} disabled={page === 0}>« First</Btn>
              <Btn small onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹ Prev</Btn>
              <Btn small onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}>Next ›</Btn>
              <Btn small onClick={() => setPage(pages - 1)} disabled={page >= pages - 1}>Last »</Btn>
            </div>
          </div>
          <div className="text-[11px] text-grayscale-1100">
            Sorting a column sorts this page only. Click an address for the person's timeline — every mail and
            what SendGrid saw happen to it. Hover a tag (Instant read, Apple MPP, Bot click) for why it was
            counted as automated.
          </div>
        </>
      )}
      {timeline && <Timeline {...timeline} onClose={() => setTimeline(null)} />}
    </div>
  );
};

export default SendsTable;
